/**
 * Utility functions for consistent error handling across the codebase.
 */

/**
 * Extracts a human-readable message from an unknown error value.
 * Handles Error instances, strings, and unknown types.
 *
 * @param error - The caught error value
 * @param fallback - Default message if error type is unrecognized
 * @returns A string error message
 */
export function extractErrorMessage(
  error: unknown,
  fallback = "An unexpected error occurred",
): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  return fallback;
}

// Native fetch failure messages across engines: Chrome ("Failed to fetch"),
// Safari ("Load failed"), Firefox ("NetworkError when attempting to fetch
// resource"), plus explicit net::ERR_* connection errors.
const NETWORK_ERROR_MESSAGE =
  /failed to fetch|networkerror|network error|load failed|connection|err_/;

/**
 * Gateway/infrastructure statuses that say "try again", not "this request is
 * wrong". A 504 from Vercel means the edge gave up waiting on the function —
 * the same request a moment later, against a warm instance, usually succeeds.
 *
 * Treating these as fatal is what turned one transient 504 into a permanent
 * "Transactions couldn't be loaded" on /finances/all (2026-08-16): the whole
 * view dead-ended on a blip that a single retry would have cleared.
 *
 * 5xx codes NOT listed here (500, 501) are deterministic application faults —
 * retrying those just doubles the load and the PostHog issue count.
 */
const RETRYABLE_HTTP_STATUS = new Set([408, 425, 429, 502, 503, 504]);

/**
 * Error carrying the HTTP status that produced it, so retry policy can tell a
 * gateway timeout apart from a deterministic 4xx. `fetch` rejections have no
 * status; a non-ok response does, and losing it is what made 504s unretryable.
 */
export class HttpStatusError extends Error {
  readonly status: number;

  constructor(message: string, status: number, options?: { cause?: unknown }) {
    super(message, options);
    this.name = "HttpStatusError";
    this.status = status;
  }
}

/**
 * A failure that is the user's next step, already worded for them: a conflict
 * ("Already confirmed — refresh to see it") or an unmet precondition. The
 * query and mutation caches still surface it, but it is not a defect, so it is
 * never sent to PostHog as an exception.
 */
export class ExpectedUserError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ExpectedUserError";
  }
}

/**
 * Whether an error is a transient client-side network failure worth retrying.
 *
 * @supabase/postgrest-js leaves `code` empty for client-side fetch failures
 * (see PostgrestBuilder: "We don't populate code/hint for client-side network
 * errors") and only sets it for upstream PostgREST/Postgres errors, which are
 * deterministic and must fast-fail. The classic case: a Supabase `.rpc()` POST
 * that hits a stale keep-alive socket surfaces `TypeError: Failed to fetch`
 * with `code: ''` — Chrome won't auto-retry a non-idempotent POST, so a single
 * app-level retry on a fresh connection is what heals it.
 *
 * Aborted requests (navigation / manual cancel) are intentionally excluded.
 */
export function isTransientNetworkError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) {
    return false;
  }
  const e = error as {
    message?: unknown;
    code?: unknown;
    name?: unknown;
    status?: unknown;
  };
  if (e.name === "AbortError") {
    return false;
  }
  // A gateway status is transient by definition — the request never reached a
  // verdict, so the same request can still succeed. Checked before `code`
  // because these carry a status rather than a PostgREST code.
  if (typeof e.status === "number" && RETRYABLE_HTTP_STATUS.has(e.status)) {
    return true;
  }
  // A non-empty code means an upstream (PostgREST/Postgres) error — never retry.
  const code = typeof e.code === "string" ? e.code : "";
  if (code !== "") {
    return false;
  }
  const message = typeof e.message === "string" ? e.message.toLowerCase() : "";
  return NETWORK_ERROR_MESSAGE.test(message);
}

/** Postgres SQLSTATE for a canceled statement (statement_timeout fired). */
const QUERY_CANCELED = "57014";
const STATEMENT_TIMEOUT_TEXT = /canceling statement due to statement timeout/i;

/**
 * Whether an error, or one of its first five causes, is a Postgres statement
 * timeout. PostgREST reports it as code 57014. Wrapped errors keep only the
 * text in `cause`.
 */
export function isStatementTimeout(error: unknown): boolean {
  let current: unknown = error;
  for (let depth = 0; depth < 6; depth += 1) {
    if (typeof current !== "object" || current === null) return false;
    const { code, message, cause } = current as {
      code?: unknown;
      message?: unknown;
      cause?: unknown;
    };
    if (code === QUERY_CANCELED) return true;
    if (typeof message === "string" && STATEMENT_TIMEOUT_TEXT.test(message))
      return true;
    current = cause;
  }
  return false;
}

/**
 * Whether an error came back from PostgREST/Postgres. Those carry a non-empty
 * SQLSTATE or PGRST `code` next to `details`/`hint`, and their message is raw
 * database text, never copy for a user. Client-side fetch failures leave
 * `code` empty (see isTransientNetworkError).
 */
export function isDatabaseError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const e = error as { code?: unknown };
  return (
    typeof e.code === "string" &&
    e.code !== "" &&
    ("details" in error || "hint" in error)
  );
}

/**
 * Type guard to check if an error has a specific error code.
 * Useful for handling Supabase or API errors with codes.
 */
export function hasErrorCode(
  error: unknown,
  code: string,
): error is Error & { code: string } {
  return (
    error instanceof Error &&
    "code" in error &&
    (error as Error & { code: string }).code === code
  );
}

/**
 * Creates a formatted error message with optional context.
 * Useful for logging or displaying errors with additional information.
 */
export function formatErrorWithContext(
  error: unknown,
  context: string,
): string {
  const message = extractErrorMessage(error);
  return `${context}: ${message}`;
}
